import { useState } from 'react';
import { useParams } from 'react-router-dom';
import Popover from '@mui/material/Popover';
import DropdownItem from '../../DropdownItem';
import { ClientApplicationStatus } from 'api/models/ClientApplicationStatus';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import * as S from '../../../Dashboard.styled';

type SelectStatusDropdownProps = {
    selectedStatus: string;
    mobile?: boolean;
    dashboard?: boolean;
    handleSelectedStatus: (status: ClientApplicationStatus | string) => void;
};

const SelectStatusDropdown = ({
    selectedStatus,
    mobile,
    dashboard,
    handleSelectedStatus,
}: SelectStatusDropdownProps) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const params = useParams();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;
    const dropdownStyles =
        params['*'] && params['*'].endsWith('dashboard')
            ? {}
            : { minWidth: '10rem', maxWidth: '10rem' };

    const convertStatusName = (statusName: ClientApplicationStatus | string) => {
        if (!statusName || statusName === t('selectStatus', { lng: language })) {
            return t('selectStatus', { lng: language });
        }

        const tempStatusName = statusName.toLowerCase().split('_').join(' ');
        const capitalizedStatusName =
            tempStatusName.charAt(0).toUpperCase() + tempStatusName.slice(1);
        return capitalizedStatusName;
    };

    const StatusesList = Object.values(ClientApplicationStatus).map(
        (status: ClientApplicationStatus) => ({
            name: convertStatusName(status),
            onClick: () => handleSelectedStatus(status),
        })
    );

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleOptionClick = (e: React.MouseEvent<HTMLElement>, selectedStatus: string) => {
        e.stopPropagation();
        const clickedOption = StatusesList.find((status) => status.name === selectedStatus);
        if (clickedOption) {
            clickedOption.onClick();
        } else {
            handleSelectedStatus('');
        }

        handleClose();
    };

    return (
        <>
            <S.LeftDropdown
                dashboard={dashboard}
                mobile={mobile}
                style={dropdownStyles}
                isActive={!!anchorEl}
                onClick={(event) => {
                    handleClick(event);
                }}
            >
                <S.SelectionContainer>{convertStatusName(selectedStatus)}</S.SelectionContainer>
                <S.DropdownIcon />
            </S.LeftDropdown>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                marginThreshold={0}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            overflow: 'visible',
                            mt: '8px',
                            minWidth: '7rem',
                            padding: '0',
                            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.09)',
                        },
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            >
                <S.DropDownMenu dashboardMenuMobile={mobile}>
                    {params['*'] && !params['*'].endsWith('/status') && (
                        <DropdownItem onClick={(e) => handleOptionClick(e, 'All')}>
                            {t('all', { lng: language })}
                        </DropdownItem>
                    )}

                    {StatusesList.map((option, i) => {
                        return (
                            <DropdownItem
                                key={i}
                                onClick={(e) => handleOptionClick(e, option.name)}
                            >
                                {option.name}
                            </DropdownItem>
                        );
                    })}
                </S.DropDownMenu>
            </Popover>
        </>
    );
};

export default SelectStatusDropdown;
